import CurrentManagementReportAnchor from "components/company/v2/CurrentManagementReportAnchor";
import useBrazilianHighlights from "components/company/v3/brazilian/boxes/hooks/useBrazilianHighlights";
import OneLinerHighlightsComment from "components/company/v3/brazilian/contents/highlights/OneLinerHighlightsComment";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import UnlockPremiumContentCTA, { TrialCTAContentForHighlights } from "components/conversion/UnlockPremiumContentCTA";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import FinancialHighlightsContent from "components/portfolio/components/FinancialHighlightsContent";
import FinancialHighlightsDisclaimer from "components/portfolio/components/FinancialHighlightsDisclaimer";
import classNames from "components/ui/classNames";
import ExpandableParagraph from "components/ui/ExpandableParagraph";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";
import useSelfie from "lib/hooks/useSelfie";
import { FormattedMessage, useIntl } from "react-intl";

export default function BrazilianHighlightsContent() {
  const issuer = useBrazilianIssuer();
  const { highlights, filterByFinancialHighlightsId, setFilterByFinancialHighlightsId, loading } =
    useBrazilianHighlights();
  const viewer = useSelfie();
  const intl = useIntl();

  if (!viewer) {
    return null;
  }

  if (loading) {
    return <LoadingSkeleton count={1} className="h-96 w-full" />;
  }

  if (!highlights || !highlights.financialHighlights) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 font-aeonik">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
          <div>
            <SadFaceIcon />
          </div>

          <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
        </div>
      </div>
    );
  }

  const candidateHighlights =
    highlights.financialHighlights.availableHighlights.length > 0
      ? highlights.financialHighlights.availableHighlights[0]._id
      : undefined;

  return (
    <div className="space-y-4">
      <div className="flex w-full flex-col-reverse items-center gap-2 xl:flex-row">
        <div className="flex w-full items-center space-x-2 xl:w-min">
          <Select
            onValueChange={(value) => setFilterByFinancialHighlightsId(value ?? null)}
            defaultValue={filterByFinancialHighlightsId ?? candidateHighlights}
          >
            <SelectTrigger
              className={classNames(
                "w-full rounded-xl border-slate-200 bg-white font-aeonik tracking-wider sm:w-32",
                "dark:border-slate-950 dark:bg-slate-800 dark:text-slate-300",
              )}
            >
              <SelectValue placeholder="Escolha o Trimestre" />
            </SelectTrigger>
            <SelectContent className="rounded-lg border border-slate-200 text-sm">
              {highlights.financialHighlights.availableHighlights.map((highlights) => (
                <SelectItem
                  key={`selectBoxYear/${highlights._id}`}
                  className="cursor-pointer rounded-lg text-sm"
                  value={highlights._id}
                >
                  {highlights.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="grid auto-cols-max grid-flow-col gap-x-1 text-sm">
            <CurrentManagementReportAnchor
              lastQuarterlyManagementReport={issuer.lastQuarterlyManagementReport}
              lastYearlyManagementReport={issuer.lastYearlyManagementReport}
              reportSource={highlights.financialHighlights.reportSource}
              className={classNames(
                "flex h-10 items-center justify-center rounded-lg bg-blue-50 px-3 font-medium text-blue-700 no-underline",
                "hover:underline",
                "dark:bg-blue-900/30 dark:text-blue-100",
              )}
            />
          </div>
        </div>
      </div>

      {!viewer.isTrialing ? (
        <div className="space-y-4 text-base tracking-wider text-gray-700 dark:text-gray-400 lg:space-y-6">
          <FinancialHighlightsContent
            key={highlights.financialHighlights.financialHighlights._id}
            content={highlights.financialHighlights.financialHighlights.highlights}
          />

          {viewer.isPremium &&
            !viewer.isTrialing &&
            highlights.financialHighlights.financialHighlights.oneLiners?.map((node) => (
              <OneLinerHighlightsComment
                key={node.author._id}
                oneLiner={node}
                analystRating={highlights.financialHighlights?.analystRatings.find(
                  (rating) => rating.analyst._id === node.author._id,
                )}
              />
            ))}

          <FinancialHighlightsDisclaimer />
        </div>
      ) : (
        <div className="space-y-4 text-base tracking-wider text-gray-700">
          <ExpandableParagraph expandable={false}>
            <FinancialHighlightsContent
              key={highlights.financialHighlights.financialHighlights._id}
              content={highlights.financialHighlights.financialHighlights.highlights}
            />
          </ExpandableParagraph>

          <FinancialHighlightsDisclaimer />

          <UnlockPremiumContentCTA
            origin="Financials Highlights"
            freeMessage={intl.formatMessage({
              defaultMessage: "Para ver os Destaques Trimestrais completos, você precisa ser Premium.",
              id: "nUejI5",
            })}
            trialMessage={<TrialCTAContentForHighlights />}
          />
        </div>
      )}
    </div>
  );
}
